/* eslint-disable no-unused-expressions */
import { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { Typography } from '@mui/material';
import { AxiosResponse } from 'axios';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastify/reducer';
import { getUserProfileSelector } from 'store/user-service/selectors';
import { CustomButton, Loader } from 'components/shared';
import {
	DEFAULT_ADD_SERVICE_VALUE,
	FIREBASE_EVENT_ANALYTICS_BUTTON,
	FIREBASE_EVENT_ANALYTICS_PAGE,
	PORTMONE_AMOUNT_FIELD_NAME,
} from 'utils/constants';
import { BILL_TYPE, ERROR_CODE, ERROR_MESSAGE, LOCALE, ROUTER_URL } from 'utils/enums';
import {
	checkIsAmountServiceField,
	convertUAHToCoins,
	findServiceProviderAccountIdValue,
	getContractNumberFieldName,
	getCreateBillRequestServiceFieldsData,
	getCreateBillServiceRequestError,
	getErrorMessage,
	getErrorResponse,
	getServicesFieldsList,
	getServicesOtherPortmoneAmountFromTariffItems,
	getServiceTariffItemsList,
	groupServiceBillRequestItemsByRecipient,
	sortServicesFieldsByFieldsNames,
} from 'utils/helpers';
import { BillService, FirebaseAnalytics } from 'utils/services';
import {
	IAddServiceFieldValueRequest,
	IBillCreateRequestArrayDataItem,
	ICreateBillRequest,
	IGetServiceDataResponse,
	INormalizedServiceBillRequestItem,
	IOtherServiceProviderServiceFieldsData,
	IPaymentPayerFormData,
	IServiceBillError,
	IServiceBillRequestData,
	IServiceBillRequestTariffItem,
	IServiceFieldResponse,
	IServicesItemResponse,
	IUpdateServiceFieldValueRequest,
	IUserProfileResponse,
	ServiceFeeEntry,
} from 'utils/types';
import { ServiceBillRequestLine } from '../ServiceBillRequestLine';
import { ProviderServiceDetails } from '../ServiceDetails';
import { ProviderServiceFieldsList } from '../ServiceFieldsList';
import { ServiceFieldsTariffItem } from '../ServiceFieldsTariffItem';
import { ProviderServiceHeader } from '../ServiceHeader';
import { ServicePaymentInstruction } from '../ServicePaymentInstruction';
import styles from './index.module.scss';

interface ServiceBillRequestFieldsProps {
	serviceData: IGetServiceDataResponse;
	serviceItemData: IServicesItemResponse;
	onChange: (value: IOtherServiceProviderServiceFieldsData) => void;
	onSubmit: () => void;
	onClickBack: () => void;
	onGetBillRequestData?: (value: IServiceBillRequestData | null) => void;
	onGetCreateBillRequestData?: (value: ICreateBillRequest) => void;
	billRequestServices: INormalizedServiceBillRequestItem[];
	selectedServicesToPay?: INormalizedServiceBillRequestItem[];
	onChangeSelectedServices: (service: INormalizedServiceBillRequestItem) => void;
	onChangeServiceAmount: (service: INormalizedServiceBillRequestItem, amount: string) => void;
	onChangeFee: (feeEntry: ServiceFeeEntry) => void;
	isShowDetails: boolean;
	paymentPayerData?: IPaymentPayerFormData | null;
	instructionNumber?: string | number;
}

const isValidBillRequestData = (
	billRequestData: IServiceBillRequestData | null
): billRequestData is IBillCreateRequestArrayDataItem =>
	!!billRequestData && !Array.isArray(billRequestData) && !('error' in billRequestData);

const hasTariffItems = (billRequestData: IBillCreateRequestArrayDataItem): boolean =>
	!!billRequestData.tariffItem?.tariff_items && !!Object.keys(billRequestData.tariffItem.tariff_items).length;

export const ServiceBillRequestFields: FC<ServiceBillRequestFieldsProps> = ({
	serviceData,
	onChange,
	serviceItemData,
	onSubmit,
	onClickBack,
	onGetBillRequestData,
	onGetCreateBillRequestData,
	billRequestServices,
	selectedServicesToPay,
	onChangeSelectedServices,
	onChangeServiceAmount,
	onChangeFee,
	isShowDetails,
	paymentPayerData,
	instructionNumber,
}) => {
	const history = useHistory();

	const dispatch = useAppDispatch();

	const profile = useSelector(getUserProfileSelector);

	const [serviceFieldsList, setServiceFieldsList] = useState<IServiceFieldResponse[]>([]);
	const [serviceFieldsValues, setServiceFieldsValues] = useState<IAddServiceFieldValueRequest[]>([]);
	const [visibleFieldsList, setVisibleFieldsList] = useState<IServiceFieldResponse[]>([]);
	const [billRequestData, setBillRequestData] = useState<IServiceBillRequestData | null>(null);
	const [isValid, setIsValid] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	const isMultipleBillsData = Array.isArray(billRequestData);

	const isFormDisabled =
		!isValid ||
		isLoading ||
		(isMultipleBillsData && !selectedServicesToPay?.filter((item) => Number(item.incomeBalance) > 0).length);

	const billRequestGroups = useMemo(
		() => groupServiceBillRequestItemsByRecipient(billRequestServices),
		[billRequestServices]
	);

	const tariffItemsList: IServiceBillRequestTariffItem[] = useMemo(() => {
		if (
			billRequestData &&
			!Array.isArray(billRequestData) &&
			!('error' in billRequestData) &&
			billRequestData.tariffItem?.tariff_items &&
			Object.keys(billRequestData.tariffItem.tariff_items).length
		) {
			return [
				{
					payee: billRequestData.tariffItem.payee,
					list: getServiceTariffItemsList(billRequestData.tariffItem.tariff_items),
					description: billRequestData.tariffItem.description,
				},
			];
		}
		return [];
	}, [billRequestData]);

	const paymentDate = useMemo(() => {
		let billItems: IBillCreateRequestArrayDataItem[] = [];

		if (Array.isArray(billRequestData)) {
			billItems = selectedServicesToPay?.length
				? selectedServicesToPay
				: billRequestData.filter((item): item is IBillCreateRequestArrayDataItem => !('error' in item));
		} else if (isValidBillRequestData(billRequestData)) {
			billItems = [billRequestData];
		}

		return Array.from(new Set(billItems.map((item) => item.tariffItem.date).filter(Boolean))).join(', ');
	}, [billRequestData, selectedServicesToPay]);

	useEffect(() => {
		if (serviceData.serviceId) {
			getCurrentServiceFields(serviceData.serviceId);
		}
	}, [serviceData.serviceId]);

	useEffect(() => {
		if (serviceFieldsList.length && !billRequestData) {
			const visibleServiceFieldsListPayload = getServicesFieldsList(serviceFieldsList, serviceData.dataObject);
			setVisibleFieldsList(visibleServiceFieldsListPayload);
		}
	}, [serviceFieldsList.length, billRequestData]);

	useEffect(() => {
		if (onGetBillRequestData) {
			onGetBillRequestData(billRequestData);
		}
	}, [billRequestData]);

	useEffect(() => {
		const serviceProviderFieldsData = serviceFieldsValues.reduce(
			(acc, item) => ({
				...acc,
				[item.name]: checkIsAmountServiceField(item) ? convertUAHToCoins(item.value).toString() : item.value,
			}),
			{} as IOtherServiceProviderServiceFieldsData
		);
		if (Object.keys(serviceProviderFieldsData).length) {
			onChange(serviceProviderFieldsData);
		}
	}, [serviceFieldsValues]);

	const getServiceFieldsValuesList = (
		serviceFieldsList: IServiceFieldResponse[],
		portmoneAmountToPay?: string
	): IAddServiceFieldValueRequest[] =>
		serviceFieldsList.map((item) => {
			const existsFieldValue = serviceFieldsValues.find((serviceFieldValue) => serviceFieldValue.name === item.name);
			const payload = existsFieldValue || {
				...DEFAULT_ADD_SERVICE_VALUE,
				serviceId: serviceData.serviceId,
				name: item.name,
				userId: (profile as IUserProfileResponse).userId,
			};
			if (checkIsAmountServiceField(item) && portmoneAmountToPay) {
				payload.value = portmoneAmountToPay;
			}
			return payload;
		});

	const getCurrentServiceFields = async (serviceId: string): Promise<void> => {
		try {
			setIsLoading(true);
			const fieldsResponse: AxiosResponse<IServiceFieldResponse[]> = await BillService.getServiceFields({
				serviceId,
				locale: LOCALE.UK,
			});
			const serviceFieldsList = getServicesFieldsList(fieldsResponse.data, serviceData.dataObject);
			setServiceFieldsList(sortServicesFieldsByFieldsNames(fieldsResponse.data));
			setServiceFieldsValues(getServiceFieldsValuesList(serviceFieldsList));
		} catch (error) {
			const errorResponse = getErrorResponse(error);
			if (errorResponse?.errorData?.code === ERROR_CODE.SERVICE_FIELDS_NOT_FOUND) {
				dispatch(showToast({ message: ERROR_MESSAGE.SERVICE_FIELDS_NOT_FOUND }));
			}
			dispatch(showToast({ message: getErrorMessage(error) }));
			history.replace(ROUTER_URL.NOT_FOUND);
		} finally {
			setIsLoading(false);
		}
	};

	const createBillRequest = async (): Promise<void> => {
		try {
			setIsLoading(true);
			const contractNumber = findServiceProviderAccountIdValue(serviceFieldsValues, serviceData);
			if (contractNumber) {
				const reqBody: ICreateBillRequest = {
					payeeId: serviceData.dataObject.payeeId,
					serviceFieldsData: getCreateBillRequestServiceFieldsData(serviceData.dataObject, serviceFieldsValues),
					billType: serviceData.billType,
					serviceId: serviceData.serviceId,
				};
				onGetCreateBillRequestData?.(reqBody);
				const response = await BillService.createBillRequest(reqBody);

				if (Array.isArray(response.data) && response.data.every((item) => !Array.isArray(item) && 'error' in item)) {
					const [firstItem] = response.data as IServiceBillError[];

					if (Array.isArray(firstItem)) return;

					dispatch(showToast({ message: firstItem.error as string }));
					return;
				}

				if (
					serviceData.billType === BILL_TYPE.FIRST ||
					(serviceData.billType === BILL_TYPE.SECOND &&
						!Array.isArray(response.data) &&
						(response.data as IBillCreateRequestArrayDataItem)?.tariffItem.tariff_items &&
						!Object.keys((response.data as IBillCreateRequestArrayDataItem)?.tariffItem.tariff_items).length)
				) {
					serviceData.sumFrom && (serviceFieldsList[1].minAmount = serviceData.sumFrom / 100);
					serviceData.sumTo && (serviceFieldsList[1].maxAmount = serviceData.sumTo / 100);
					setVisibleFieldsList(serviceFieldsList);
					const amount = (response.data as IBillCreateRequestArrayDataItem)?.tariffItem.amount;
					setServiceFieldsValues(getServiceFieldsValuesList(serviceFieldsList, Number(amount) ? amount : ''));
				}
				if (
					(serviceData.billType === BILL_TYPE.SECOND &&
						!Array.isArray(response.data) &&
						(response.data as IBillCreateRequestArrayDataItem)?.tariffItem.tariff_items &&
						Object.keys((response.data as IBillCreateRequestArrayDataItem)?.tariffItem).length) ||
					serviceData.billType === BILL_TYPE.THIRD
				) {
					const serviceFieldsValues = getServiceFieldsValuesList(serviceFieldsList);
					const fieldsValuesList = serviceFieldsValues.find(checkIsAmountServiceField)
						? serviceFieldsValues
						: [
								...serviceFieldsValues,
								{
									...DEFAULT_ADD_SERVICE_VALUE,
									serviceId: serviceData.serviceId,
									name: PORTMONE_AMOUNT_FIELD_NAME,
									userId: (profile as IUserProfileResponse).userId,
								},
						  ];
					const servicesFieldsValuesPayload: IAddServiceFieldValueRequest[] = fieldsValuesList.map((item) =>
						checkIsAmountServiceField(item)
							? {
									...item,
									value: getServicesOtherPortmoneAmountFromTariffItems(response.data).toFixed(2),
							  }
							: item
					);
					setServiceFieldsValues(servicesFieldsValuesPayload);
				}
				if (
					serviceData.billType === BILL_TYPE.SECOND &&
					!Array.isArray(response.data) &&
					(response.data as IBillCreateRequestArrayDataItem)?.tariffItem.amount &&
					!Number((response.data as IBillCreateRequestArrayDataItem)?.tariffItem?.amount) &&
					!Object.keys((response.data as IBillCreateRequestArrayDataItem)?.tariffItem.tariff_items).length
				) {
					dispatch(
						showToast({
							message: `${ERROR_MESSAGE.SERVICE_BILL_REQUEST_PAID_ALREADY}, або сплатіть довільну суму`,
							duration: 5000,
							color: 'light',
						})
					);
				}
				setBillRequestData(response.data);

				const shouldSkipIntermediateStep =
					isValidBillRequestData(response.data as IServiceBillRequestData) &&
					!hasTariffItems(response.data as IBillCreateRequestArrayDataItem) &&
					!serviceFieldsList.some(checkIsAmountServiceField);

				if (shouldSkipIntermediateStep) {
					onSubmit();
				}
			}
		} catch (error) {
			dispatch(showToast({ message: getCreateBillServiceRequestError(error) }));
		} finally {
			setIsLoading(false);
		}
	};

	const handleSubmit = (): void => {
		if (isFormDisabled) return;

		if (!billRequestData) {
			FirebaseAnalytics.logEvent(
				FIREBASE_EVENT_ANALYTICS_PAGE.PROFILE.SERVICES,
				FIREBASE_EVENT_ANALYTICS_BUTTON.CONTINUE
			);
			createBillRequest();
		} else {
			onSubmit();
		}
	};

	const handleClickBack = (): void => {
		if (isShowDetails || !billRequestData) {
			onClickBack();
			return;
		}

		const visibleServiceFieldsListPayload = getServicesFieldsList(serviceFieldsList, serviceData.dataObject);
		setVisibleFieldsList(visibleServiceFieldsListPayload);
		setServiceFieldsValues(getServiceFieldsValuesList(visibleServiceFieldsListPayload));
		setBillRequestData(null);
	};

	return (
		<>
			<ProviderServiceHeader
				onClickBack={handleClickBack}
				title={billRequestGroups.length === 1 ? billRequestGroups[0].recipientName : serviceItemData.title}
				icon={serviceItemData?.imageUrl ?? ''}
			/>
			{!!(
				visibleFieldsList.length &&
				!isShowDetails &&
				(billRequestData
					? serviceData.billType === BILL_TYPE.FIRST ||
					  (!Array.isArray(billRequestData) &&
							(billRequestData as IBillCreateRequestArrayDataItem)?.tariffItem.tariff_items &&
							!Object.keys((billRequestData as IBillCreateRequestArrayDataItem)?.tariffItem.tariff_items).length)
					: !billRequestData)
			) && (
				<ProviderServiceFieldsList
					onSubmit={handleSubmit}
					fieldsList={visibleFieldsList}
					values={serviceFieldsValues}
					serviceId={serviceData.serviceId}
					setIsValid={(value: boolean) => setIsValid(value)}
					onChange={(values: IAddServiceFieldValueRequest[] | IUpdateServiceFieldValueRequest[]) =>
						setServiceFieldsValues(values as IAddServiceFieldValueRequest[])
					}
					providerAccountIdFieldName={getContractNumberFieldName(serviceData)}
					isFieldsDisabled={
						serviceData.billType !== BILL_TYPE.THIRD && visibleFieldsList.length === serviceFieldsList.length
						// ||
						// (!!billRequestData?.tariff_items && !Object.keys(billRequestData?.tariff_items).length)
					}
				/>
			)}
			{!!(isMultipleBillsData && !isShowDetails && billRequestGroups.length) && (
				<div className={styles.groups}>
					{billRequestGroups.map((group) => (
						<section className={styles.group} key={group.recipientName}>
							<div className={styles.group__header}>
								<Typography variant="subtitle1">{group.recipientName}</Typography>
								<Typography variant="caption">Послуг: {group.services.length}</Typography>
							</div>
							<div className={styles.group__list}>
								{group.services.map((service) => (
									<ServiceBillRequestLine
										key={service.selectionKey}
										service={service}
										isSelected={
											!!selectedServicesToPay?.some(
												(selectedService) => selectedService.selectionKey === service.selectionKey
											)
										}
										onSelect={onChangeSelectedServices}
										onChangeAmount={onChangeServiceAmount}
										onChangeFee={onChangeFee}
									/>
								))}
							</div>
						</section>
					))}
				</div>
			)}
			{!!(billRequestData && !isMultipleBillsData && serviceData.billType !== BILL_TYPE.FIRST && !isShowDetails) &&
				tariffItemsList.map((tariffItem, index) => (
					<ServiceFieldsTariffItem
						onChangeFee={onChangeFee}
						key={`tarif-list-${tariffItem.payee.$.id}-${index}`}
						tariffItem={tariffItem}
						onSelect={() => undefined}
						isSelected={false}
					/>
				))}
			{isShowDetails && isMultipleBillsData && (
				<ServicePaymentInstruction
					services={selectedServicesToPay ?? []}
					payerData={paymentPayerData}
					icon={serviceItemData.imageUrl ?? ''}
				/>
			)}
			{isShowDetails && !isMultipleBillsData && (
				<ProviderServiceDetails
					title="Деталі платежу"
					showBankName
					data={isValidBillRequestData(billRequestData) ? billRequestData : serviceData}
					providerAccountId={findServiceProviderAccountIdValue(serviceFieldsValues, serviceData)}
					payerData={paymentPayerData}
					paymentDate={paymentDate}
					instructionNumber={instructionNumber}
				/>
			)}

			{!!visibleFieldsList.length && !isShowDetails && (
				<CustomButton disabled={isFormDisabled} label="Продовжити" onClick={handleSubmit} />
			)}
			{isLoading && <Loader />}
		</>
	);
};
